import { useDraggable } from "@dnd-kit/core";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { useStore } from "@nanostores/react";
import { KeyboardEvent, ReactElement, useCallback } from "react";

import { Entity } from "../../api";
import { getViewportCenter } from "../../services/graph/get-viewport-center.ts";
import { createGraphNodeModel } from "../../services/graph/user-actions/create-graph-node-model.ts";
import { nodeOperations } from "../../services/graph/user-actions/node-operations.ts";
import { $cytoscapeInstance } from "../../stores/graph/ui.ts";
import { $router } from "../../stores/router.ts";
import { getStickyNoteEntity } from "../../utils/sticky-note-utils.ts";

export const FloatingStickyNote = (): ReactElement => {
  const router = useStore($router);
  const cytoscapeInstance = useStore($cytoscapeInstance);
  const graphId = router?.route === "graph" ? router.params.id : null;

  // Get static sticky note entity (synchronous, no async needed)
  const stickyNoteEntity = getStickyNoteEntity();

  const handleStickyNoteEnter = useCallback(
    (entity: Entity) => {
      if (!cytoscapeInstance || !graphId) return;

      const position = getViewportCenter(cytoscapeInstance);

      const newNode = createGraphNodeModel({
        entity,
        position,
        value:
          entity.defaultValue === "" || entity.defaultValue == null
            ? "<value>"
            : entity.defaultValue,
      });

      if (newNode) {
        nodeOperations.addToGraph(graphId, newNode);
      }
    },
    [cytoscapeInstance, graphId],
  );

  const { attributes, listeners, setNodeRef } = useDraggable({
    data: {
      entity: stickyNoteEntity,
      text: "Note",
    },
    disabled: !stickyNoteEntity, // Disable dragging if entity is not available
    id: `floating-notes-${stickyNoteEntity?.id || "sticky-note"}`,
  });

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" && stickyNoteEntity) {
        handleStickyNoteEnter(stickyNoteEntity);
      }
    },
    [stickyNoteEntity, handleStickyNoteEnter],
  );

  return (
    <div
      ref={stickyNoteEntity ? setNodeRef : undefined}
      {...(stickyNoteEntity ? listeners : {})}
      {...(stickyNoteEntity ? attributes : {})}
      className={stickyNoteEntity ? "cursor-grab" : "cursor-not-allowed"}
      onKeyDown={stickyNoteEntity ? handleKeyDown : undefined}
      role="button"
      tabIndex={stickyNoteEntity ? 0 : -1}
    >
      <PencilSquareIcon
        className={`h-6 w-6 ${
          stickyNoteEntity
            ? "text-zinc-400 hover:text-zinc-200"
            : "text-zinc-600 opacity-50"
        }`}
      />
    </div>
  );
};
